import React, { FC, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProfessionalInfoSection } from './components/ProfessionalInfoSection';
import { IntroductionVideoSection } from './components/IntroductionVideoSection';
import { CareerHighlightsSection } from './components/CareerHighlightsSection';
import { ExpertiseSection } from './components/ExpertiseSection';
import { TrainingStyleSection } from './components/TrainingStyleSection';
import { SessionPricingSection } from './components/SessionPricingSection';
import { LocationFacilitySection } from './components/LocationFacilitySection';
import { SocialLinksSection } from './components/SocialLinksSection';
import { Packages } from './components/Packages/Packages';
import { TrainerSettings, SETTINGS_OPTIONS } from './components/TrainerSettings/TrainerSettings';
import { CompleteOnboardingBanner } from './components/CompleteOnboardingBanner/CompleteOnboardingBanner';
import { ProfileEditProvider, ProfileEditContext } from './ProfileEditContext';
import './Profile.scss';
import { ReactComponent as Pencil2Icon } from '../../../../../../assets/images/onboarding-trainer/dashboard/profile/pencil2.svg';

type ProfileTab = 'profile' | 'packages' | 'settings';

const ProfileContent: FC = () => {
  const { isBulkEditing, toggleAllEdits } = useContext(ProfileEditContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabParam = searchParams.get('tab');
  const sectionParam =
    searchParams.get('section') || searchParams.get('subtab') || searchParams.get('subTab');

  const isSettingsOption =
    Boolean(sectionParam) ||
    Boolean(
      currentTabParam &&
      SETTINGS_OPTIONS.some(
        (opt) =>
          opt.id === currentTabParam.toLowerCase().replace(/[\s_]+/g, '-') ||
          opt.label.toLowerCase() === currentTabParam.toLowerCase(),
      ),
    );

  const activeTab: ProfileTab =
    currentTabParam === 'packages'
      ? 'packages'
      : currentTabParam === 'settings' || isSettingsOption
        ? 'settings'
        : 'profile';

  const isPackagesSubflow =
    activeTab === 'packages' &&
    (searchParams.get('view') === 'create' ||
      searchParams.get('view') === 'edit' ||
      searchParams.get('view') === 'template');

  const handleTabChange = (tab: ProfileTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'profile') {
        next.delete('tab');
        next.delete('section');
        next.delete('subtab');
        next.delete('subTab');
      } else if (tab === 'packages') {
        next.set('tab', 'packages');
        next.delete('section');
        next.delete('subtab');
        next.delete('subTab');
      } else {
        next.set('tab', 'settings');
        const currentSection = next.get('section') || next.get('subtab');
        if (!currentSection) {
          next.set('section', 'schedule');
        }
      }
      next.delete('view');
      next.delete('id');
      next.delete('type');
      return next;
    });
  };

  return (
    <div className="profile-container">
      {/* Top Tab Bar for Mobile & Desktop (Hidden during package create/edit/template flows) */}
      {!isPackagesSubflow && (
        <div className="profile-top-tabs-bar">
          <CompleteOnboardingBanner />
          <div className="profile-top-header-row">
            {activeTab === 'profile' && (
              <>
                <div className="header-text">
                  <h1>{'Profile'}</h1>
                  <p>{'Manage your account information'}</p>
                </div>
                <button className="edit-profile-btn" onClick={toggleAllEdits}>
                  <Pencil2Icon />
                  {isBulkEditing ? 'Cancel Editing' : 'Edit Profile'}
                </button>
              </>
            )}

            {activeTab === 'packages' && (
              <div className="header-text">
                <h1>{'Packages'}</h1>
                <p>{'Your can create a maximum of 3 packages'}</p>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="header-text">
                <h1>{'Settings'}</h1>
                <p>
                  {
                    'Manage your account preferences, notification preferences, and platform configurations in one place.'
                  }
                </p>
              </div>
            )}
          </div>

          <div className="profile-tabs-nav">
            <button
              type="button"
              className={`profile-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => handleTabChange('profile')}
            >
              {'My Profile'}
            </button>
            <div className="tab-separator" />
            <button
              type="button"
              className={`profile-tab-btn ${activeTab === 'packages' ? 'active' : ''}`}
              onClick={() => handleTabChange('packages')}
            >
              {'Packages'}
            </button>
            <div className="tab-separator" />
            <button
              type="button"
              className={`profile-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('settings')}
            >
              {'Settings'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="profile-content">
          <ProfessionalInfoSection />
          <IntroductionVideoSection />
          <CareerHighlightsSection />
          <ExpertiseSection />
          <TrainingStyleSection />
          <SessionPricingSection />
          <LocationFacilitySection />
          <SocialLinksSection />
        </div>
      )}

      {activeTab === 'packages' && <Packages />}

      {activeTab === 'settings' && <TrainerSettings />}
    </div>
  );
};

export const Profile: FC = () => {
  return (
    <ProfileEditProvider>
      <ProfileContent />
    </ProfileEditProvider>
  );
};
